import type { Context, Result, Statement, StatementObject, Verb } from '@xapi/xapi'
import lodash from 'lodash'

import type { IExtensionsType, ILmsEvent, IProviderConfig } from '../types'

export const LOCAL_BASE_IRI = 'https://parta.io/xapi/'

const COURSE_VERBS = ['started', 'passed', 'failed', 'completed', 'answer', 'attempted']

const getExtensionsMapOfObject = ({
  endpoint = '',
  data = {},
}: {
  endpoint?: string
  data?: IExtensionsType
}): IExtensionsType => {
  const newObject: IExtensionsType = {}
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      newObject[`${endpoint}${key}`] = value
    }
  }
  return newObject
}

export class StatementBuilder {
  private config: IProviderConfig

  constructor(config: IProviderConfig) {
    this.config = config
  }

  static fromProviderConfig(config: IProviderConfig): StatementBuilder {
    const { auth, endpoint = LOCAL_BASE_IRI, ...rest } = config
    return new StatementBuilder({ ...rest, endpoint })
  }

  updateConfig(config: Partial<IProviderConfig>): void {
    this.config = { ...this.config, ...config }
  }

  buildStatement(event: ILmsEvent, isLocal: boolean): Statement {
    const {
      actor,
      activityId,
      registration,
      endpoint,
      courseId,
      uuid,
      sessionId,
      exportId,
      prefixId,
      embedId,
    } = this.config
    const effectiveEndpoint = isLocal ? LOCAL_BASE_IRI : endpoint || LOCAL_BASE_IRI
    // Why??? -----------------------------
    // const preparedPrefixId = isLocal ? effectiveEndpoint : prefixId
    // const preparedId = isLocal
    //   ? `${effectiveEndpoint}activities/element/${event.elementId || activityId}`
    //   : activityId
    // const contextId = isLocal
    //   ? `${effectiveEndpoint}activities/course/${activityId}`
    //   : activityId
    // ------------------------------------
    const verb: Verb = {
      id: `${prefixId}verbs/${event.verb}`,
      display: { 'en-US': event.verb },
    }
    const preparedObjectExtensions: IExtensionsType = {
      ...event.objectExtensions,
      // TODO: implicitly adding variables to object extensions, need to decide in types packet courseAnalytics and try to move analytics types
      activityId: uuid,
      courseId,
      exportId,
      embedId,
      timestamp: new Date().getTime(),
      eventName: event.elementType || event.verb,
    }

    const object: StatementObject = {
      objectType: 'Activity',
      id: activityId,
      definition: {
        type: `${effectiveEndpoint}activity-types/${event.elementType || 'course'}`,
        name: { 'en-US': event.elementType || 'course' },
        extensions: getExtensionsMapOfObject({
          endpoint: `${effectiveEndpoint}extensions/`,
          data: preparedObjectExtensions,
        }),
      },
    }

    const contextExtensions: IExtensionsType = {}
    if (sessionId) {
      contextExtensions[`${LOCAL_BASE_IRI}extensions/sessionId`] = sessionId
    }

    const context: Context = {
      contextActivities: { parent: [{ id: activityId }] },
      registration,
      ...(Object.keys(contextExtensions).length > 0 && { extensions: contextExtensions }),
    }

    const isCourseData = COURSE_VERBS.includes(event.verb)
    const result: Result = isCourseData
      ? getExtensionsMapOfObject({ data: event.resultExtensions })
      : {
          extensions: getExtensionsMapOfObject({
            endpoint: effectiveEndpoint,
            data: event.resultExtensions,
          }),
        }

    const defaultConfig = {
      actor,
      object: { id: activityId, objectType: 'Activity' as const },
      context: { registration },
      timestamp: new Date().toISOString(),
    }

    return lodash.merge({}, defaultConfig, { verb, object, context, result })
  }
}
